import React, { useState, useEffect, createContext, useContext } from 'react';
import { useTour } from '@reactour/tour';

// Context to share "don't show again" state
const TourPreferencesContext = createContext({
  dontShowAgain: false,
  setDontShowAgain: () => {},
});

export const useTourPreferences = () => useContext(TourPreferencesContext);

export const TourPreferencesProvider = ({ children }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  
  return (
    <TourPreferencesContext.Provider value={{ dontShowAgain, setDontShowAgain }}>
      {children}
    </TourPreferencesContext.Provider>
  );
};

// Custom content wrapper that adds the "Don't show again" checkbox
const TourStepContent = ({ children, isLastStep }) => {
  const { dontShowAgain, setDontShowAgain } = useTourPreferences();
  const { setIsOpen } = useTour();
  
  return (
    <div>
      {children}
      {!isLastStep ? (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-500 hover:text-slate-700">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span>Don&apos;t show this tour again</span>
          </label>
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <button
            onClick={() => setIsOpen(false)}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            Close Tour
          </button>
        </div>
      )}
    </div>
  );
};

// Tour steps configuration with wrapper
export const getTourSteps = (hasAISuggestions = true, isGuidedMode = true) => {
  const wrapContent = (content, isLast = false) => (
    <TourStepContent isLastStep={isLast}>
      {content}
    </TourStepContent>
  );
  
  const steps = [
    // Step 1: Welcome / Overview
    {
      selector: '[data-testid="cleaning-options-panel"]',
      content: wrapContent(
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            👋 Welcome to the Cleaning Studio!
          </h3>
          <p className="text-slate-600">This is where you configure how each column should be cleaned.</p>
          <p className="text-indigo-600 font-medium">Let&apos;s take a quick tour of the key features!</p>
        </div>
      ),
    },
    // Step 2: Column Navigation (using the column list)
    {
      selector: '[data-testid="column-list-nav"]',
      content: wrapContent(
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            📋 Navigate Between Columns
          </h3>
          <p className="text-slate-600">Click on any column name to switch to it and configure cleaning rules.</p>
          <p className="text-slate-500 text-xs">
            Columns with <span className="text-emerald-500">✓</span> have rules configured. 
            Look for <span className="text-amber-500">⚠</span> for issues detected.
          </p>
        </div>
      ),
      position: 'right',
    },
    // Step 3: Sample Values
    {
      selector: '[data-testid="sample-values-section"]',
      content: wrapContent(
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            👀 Preview Your Data
          </h3>
          <p className="text-slate-600">See sample values from this column to understand what needs cleaning.</p>
          <p className="text-slate-500 text-xs">This helps you choose the right cleaning rules.</p>
        </div>
      ),
      position: 'bottom',
    },
  ];

  // Step 4: AI Suggestions (only if present)
  if (hasAISuggestions) {
    steps.push({
      selector: '[data-testid="ai-suggestions-section"]',
      content: wrapContent(
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            ✨ AI-Powered Suggestions
          </h3>
          <p className="text-slate-600">Our AI analyzes your data and recommends the best cleaning rules.</p>
          <p className="text-emerald-600 font-medium">Click any suggestion to instantly apply it!</p>
        </div>
      ),
      position: 'bottom',
    });
  }

  // Step 5: All Rules Section or Show More Button
  steps.push({
    selector: '[data-testid="show-more-rules-btn"], [data-testid="all-rules-section"]',
    content: wrapContent(
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          🔧 All Available Rules
        </h3>
        <p className="text-slate-600">Don&apos;t see the right suggestion? Browse all available cleaning rules here.</p>
        <p className="text-slate-500 text-xs">Rules are organized by category: Format, Standardize, Custom, and more.</p>
      </div>
    ),
    position: 'bottom',
  });

  // Step 6: Remove Column (only in guided mode)
  if (isGuidedMode) {
    steps.push({
      selector: '[data-testid="remove-column-btn"]',
      content: wrapContent(
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            🗑️ Remove Unwanted Columns
          </h3>
          <p className="text-slate-600">Don&apos;t need to clean this column? Click <strong>Remove</strong> to skip it.</p>
          <p className="text-slate-500 text-xs">You&apos;ll automatically move to the next column.</p>
        </div>
      ),
      position: 'bottom',
    });
  }

  // Step 7: Add Column
  steps.push({
    selector: '[data-testid="add-column-btn"]',
    content: wrapContent(
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          ➕ Add New Columns
        </h3>
        <p className="text-slate-600">Need to create a new column from existing data?</p>
        <p className="text-indigo-600 font-medium">Click here to add derived columns like &quot;First Name&quot; from a &quot;Full Name&quot; column.</p>
      </div>
    ),
    position: 'bottom',
  });

  // Final step: Preview & Clean (no checkbox on last step)
  steps.push({
    selector: '[data-testid="preview-changes-btn"]',
    content: wrapContent(
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          🚀 Ready to Clean!
        </h3>
        <p className="text-slate-600">Once you&apos;ve configured your rules, click <strong>Preview Changes</strong> to see the results.</p>
        <p className="text-emerald-600 font-medium">You&apos;ll see a before/after comparison before finalizing!</p>
      </div>
    , true), // isLastStep = true
    position: 'top',
  });

  return steps;
};

// ProductTour component that controls the tour
const ProductTour = ({ 
  run = false, 
  onComplete,
  hasAISuggestions = true,
  isGuidedMode = true
}) => {
  const { setIsOpen, setCurrentStep, setSteps } = useTour();

  useEffect(() => {
    // Update steps when props change
    const steps = getTourSteps(hasAISuggestions, isGuidedMode);
    setSteps(steps);
  }, [hasAISuggestions, isGuidedMode, setSteps]);

  useEffect(() => {
    if (run) {
      setCurrentStep(0);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [run, setIsOpen, setCurrentStep]);

  // This component doesn't render anything - it just controls the tour via context
  return null;
};

export default ProductTour;
